import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLogout, useGetIdentity } from "@refinedev/core";
import { LayoutDashboard, Layers, Bot, Database, Store, Workflow, LogOut, Menu, X, Hexagon, Compass } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { DevRoleSwitcher } from "@/components/dev-role-switcher";
import { NotificationBell } from "@/components/notifications/notification-bell";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "工作台" },
  { to: "/guide", icon: Compass, label: "快速开始" },
  { to: "/dashboard", icon: LayoutDashboard, label: "应用管理" },
  { to: "/builder", icon: Layers, label: "应用设计器" },
  { to: "/copilot", icon: Bot, label: "AI 副驾驶" },
  { to: "/data", icon: Database, label: "数据管理" },
  { to: "/marketplace", icon: Store, label: "交易市场" },
  { to: "/workflows", icon: Workflow, label: "工作流" },
];

interface Identity {
  id: string;
  name?: string;
  avatar?: string;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { mutate: logout } = useLogout();
  const { data: identity } = useGetIdentity<Identity>();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-gradient-to-br from-background to-muted/30">
      {/* Sidebar desktop */}
      <aside className="hidden w-64 flex-col border-r bg-card/80 backdrop-blur-md md:flex">
        <div className="flex h-16 items-center border-b px-5">
          <Link to="/" className="group flex items-center gap-3 font-semibold transition-transform hover:scale-[1.02]">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-md shadow-indigo-500/20 transition-shadow group-hover:shadow-indigo-500/40">
              <Hexagon className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col">
              <span className="text-lg leading-tight">罢歌亭</span>
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">DebugPavilion</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                  active
                    ? "bg-gradient-to-r from-indigo-500/10 to-violet-500/10 text-indigo-700 dark:text-indigo-300"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {active && (
                  <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-indigo-500 to-violet-500" />
                )}
                <span
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                    active ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400" : "bg-muted group-hover:bg-background",
                  )}
                >
                  <Icon className="h-4 w-4" />
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="m-3 space-y-3 rounded-xl border bg-card/50 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-semibold text-white shadow-md">
              {(identity?.name ?? "U").charAt(0)}
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-semibold">{identity?.name ?? "用户"}</p>
              <p className="truncate text-xs text-muted-foreground">{identity?.id ?? ""}</p>
            </div>
          </div>
          <DevRoleSwitcher />
          <Button variant="outline" className="w-full transition-colors hover:bg-destructive/10 hover:text-destructive" size="sm" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            退出
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b bg-card/80 px-4 backdrop-blur-md md:hidden">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600">
              <Hexagon className="h-4 w-4 text-white" strokeWidth={1.5} />
            </div>
            <span>罢歌亭</span>
          </Link>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </header>
        {/* Desktop top bar（通知铃铛等全局操作位） */}
        <header className="hidden h-14 items-center justify-end border-b bg-card/80 px-4 backdrop-blur-md md:flex">
          <NotificationBell />
        </header>
        {mobileOpen && (
          <div className="border-b bg-card p-3 md:hidden">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium",
                    active ? "bg-gradient-to-r from-indigo-500 to-violet-600 text-white" : "text-muted-foreground hover:bg-accent",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
            <Button variant="outline" className="mt-3 w-full" size="sm" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              退出
            </Button>
          </div>
        )}
        <main className="flex-1 overflow-auto p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}
